import {
  PerformanceReviewType,
  PerformanceRating,
  ReviewStatus,
  StudentGrade,
  StaffDepartment,
  ExternalPartnerType,
} from './performance.interfaces';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';

// Review type options
export const REVIEW_TYPE_OPTIONS = [
  { value: PerformanceReviewType.STUDENTS, label: 'Students' },
  { value: PerformanceReviewType.STAFF, label: 'Staff' },
  { value: PerformanceReviewType.EXTERNAL, label: 'External Partners' },
];

// Status options
export const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: ReviewStatus.PENDING, label: 'Pending' },
  { value: ReviewStatus.IN_PROGRESS, label: 'In Progress' },
  { value: ReviewStatus.COMPLETED, label: 'Completed' },
  { value: ReviewStatus.OVERDUE, label: 'Overdue' },
];

// Rating options
export const RATING_OPTIONS = [
  { value: 'all', label: 'All Ratings' },
  { value: PerformanceRating.EXCELLENT, label: 'Excellent' },
  { value: PerformanceRating.GOOD, label: 'Good' },
  { value: PerformanceRating.SATISFACTORY, label: 'Satisfactory' },
  { value: PerformanceRating.NEEDS_IMPROVEMENT, label: 'Needs Improvement' },
  { value: PerformanceRating.UNSATISFACTORY, label: 'Unsatisfactory' },
];

// Grade options for students
export const GRADE_OPTIONS = [
  { value: 'all', label: 'All Grades' },
  { value: StudentGrade.GRADE_1, label: 'Grade 1' },
  { value: StudentGrade.GRADE_2, label: 'Grade 2' },
  { value: StudentGrade.GRADE_3, label: 'Grade 3' },
  { value: StudentGrade.GRADE_4, label: 'Grade 4' },
  { value: StudentGrade.GRADE_5, label: 'Grade 5' },
  { value: StudentGrade.GRADE_6, label: 'Grade 6' },
  { value: StudentGrade.GRADE_7, label: 'Grade 7' },
  { value: StudentGrade.GRADE_8, label: 'Grade 8' },
  { value: StudentGrade.GRADE_9, label: 'Grade 9' },
  { value: StudentGrade.GRADE_10, label: 'Grade 10' },
  { value: StudentGrade.GRADE_11, label: 'Grade 11' },
  { value: StudentGrade.GRADE_12, label: 'Grade 12' },
];

// Department options for staff
export const DEPARTMENT_OPTIONS = [
  { value: 'all', label: 'All Departments' },
  { value: StaffDepartment.TEACHING, label: 'Teaching' },
  { value: StaffDepartment.ADMINISTRATION, label: 'Administration' },
  { value: StaffDepartment.SUPPORT, label: 'Support' },
  { value: StaffDepartment.MANAGEMENT, label: 'Management' },
];

// Partner type options for externals
export const PARTNER_TYPE_OPTIONS = [
  { value: 'all', label: 'All Partner Types' },
  { value: ExternalPartnerType.VENDOR, label: 'Vendor' },
  { value: ExternalPartnerType.CONTRACTOR, label: 'Contractor' },
  { value: ExternalPartnerType.CONSULTANT, label: 'Consultant' },
  { value: ExternalPartnerType.SERVICE_PROVIDER, label: 'Service Provider' },
];

// Helper functions
export const getRatingColor = (rating: PerformanceRating): string => {
  switch (rating) {
    case PerformanceRating.EXCELLENT:
      return '#4CAF50'; // Green
    case PerformanceRating.GOOD:
      return '#8BC34A'; // Light Green
    case PerformanceRating.SATISFACTORY:
      return '#FFC107'; // Amber
    case PerformanceRating.NEEDS_IMPROVEMENT:
      return '#FF9800'; // Orange
    case PerformanceRating.UNSATISFACTORY:
      return '#F44336'; // Red
    default:
      return '#9E9E9E'; // Grey
  }
};

export const getStatusColor = (status: ReviewStatus): string => {
  switch (status) {
    case ReviewStatus.COMPLETED:
      return '#4CAF50'; // Green
    case ReviewStatus.IN_PROGRESS:
      return '#2196F3'; // Blue
    case ReviewStatus.PENDING:
      return '#FF9800'; // Orange
    case ReviewStatus.OVERDUE:
      return '#F44336'; // Red
    default:
      return '#9E9E9E'; // Grey
  }
};

export const getRatingLabel = (
  rating: PerformanceRating,
  translateText?: TTranslateTextType
): string => {
  if (!translateText) {
    const option = RATING_OPTIONS.find((opt) => opt.value === rating);
    return option?.label || rating;
  }
  const ratingKey = rating.toLowerCase().replace('_', '');
  return translateText(`analytics.performance.options.${ratingKey}`);
};

export const getStatusLabel = (
  status: ReviewStatus,
  translateText?: TTranslateTextType
): string => {
  if (!translateText) {
    const option = STATUS_OPTIONS.find((opt) => opt.value === status);
    return option?.label || status;
  }
  const statusKey = status.toLowerCase().replace('_', '');
  return translateText(`analytics.performance.options.${statusKey}`);
};

export const formatReviewType = (
  type: PerformanceReviewType,
  translateText?: TTranslateTextType
): string => {
  if (!translateText) {
    const option = REVIEW_TYPE_OPTIONS.find((opt) => opt.value === type);
    return option?.label || type;
  }
  if (type === PerformanceReviewType.EXTERNAL) {
    return translateText('analytics.performance.options.externalPartners');
  }
  return translateText(`analytics.performance.options.${type}`);
};

export const formatScore = (score: number): string => {
  return `${score.toFixed(1)}%`;
};

export const calculateGPA = (score: number): number => {
  if (score >= 90) return 10.0;
  if (score >= 80) return 9.0;
  if (score >= 70) return 8.0;
  if (score >= 60) return 7.0;
  if (score >= 50) return 6.0;
  if (score >= 40) return 5.0;
  return 4.0;
};

export const getGradeFromScore = (score: number): string => {
  if (score >= 90) return 'A+';
  if (score >= 80) return 'A';
  if (score >= 70) return 'B+';
  if (score >= 60) return 'B';
  if (score >= 50) return 'C+';
  if (score >= 40) return 'C';
  return 'D';
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const isOverdue = (nextReviewDate: string): boolean => {
  const today = new Date();
  const reviewDate = new Date(nextReviewDate);
  return reviewDate < today;
};

export const getDaysUntilReview = (nextReviewDate: string): number => {
  const today = new Date();
  const reviewDate = new Date(nextReviewDate);
  const diffTime = reviewDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const getReviewTypeIcon = (type: PerformanceReviewType): string => {
  switch (type) {
    case PerformanceReviewType.STUDENTS:
      return '👨‍🎓';
    case PerformanceReviewType.STAFF:
      return '👨‍🏫';
    case PerformanceReviewType.EXTERNAL:
      return '🤝';
    default:
      return '📊';
  }
};

export const calculatePerformanceTrend = (
  currentScore: number,
  previousScore: number
): {
  trend: 'up' | 'down' | 'stable';
  percentage: number;
} => {
  if (previousScore === 0) {
    return { trend: 'stable', percentage: 0 };
  }

  const difference = currentScore - previousScore;
  const percentage = Math.abs((difference / previousScore) * 100);

  if (difference > 2) {
    return { trend: 'up', percentage };
  } else if (difference < -2) {
    return { trend: 'down', percentage };
  } else {
    return { trend: 'stable', percentage };
  }
};

export const generateReviewSummary = (
  reviews: any[]
): {
  totalReviews: number;
  averageScore: number;
  completionRate: number;
  topPerformers: any[];
  needsAttention: any[];
} => {
  const totalReviews = reviews.length;
  const completedReviews = reviews.filter(
    (r) => r.status === ReviewStatus.COMPLETED
  );
  const averageScore =
    reviews.reduce((sum, r) => sum + r.overallScore, 0) / totalReviews || 0;
  const completionRate = (completedReviews.length / totalReviews) * 100 || 0;

  const topPerformers = reviews
    .filter((r) => r.overallScore >= 85)
    .sort((a, b) => b.overallScore - a.overallScore)
    .slice(0, 5);

  const needsAttention = reviews
    .filter((r) => r.overallScore < 70 || r.status === ReviewStatus.OVERDUE)
    .sort((a, b) => a.overallScore - b.overallScore)
    .slice(0, 5);

  return {
    totalReviews,
    averageScore,
    completionRate,
    topPerformers,
    needsAttention,
  };
};

export const exportToCSV = (data: any[], filename: string): void => {
  if (!data.length) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      headers.map((header) => `"${row[header] || ''}"`).join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const validateReviewData = (
  reviewData: any,
  translateText?: TTranslateTextType
): string[] => {
  const errors: string[] = [];

  if (!reviewData.name?.trim()) {
    errors.push(
      translateText
        ? translateText('analytics.performance.validation.nameRequired')
        : 'Name is required'
    );
  }

  if (!reviewData.reviewer?.trim()) {
    errors.push(
      translateText
        ? translateText('analytics.performance.validation.reviewerRequired')
        : 'Reviewer is required'
    );
  }

  if (!reviewData.reviewDate) {
    errors.push(
      translateText
        ? translateText('analytics.performance.validation.reviewDateRequired')
        : 'Review date is required'
    );
  }

  if (!reviewData.overallRating) {
    errors.push(
      translateText
        ? translateText(
            'analytics.performance.validation.overallRatingRequired'
          )
        : 'Overall rating is required'
    );
  }

  if (reviewData.overallScore < 0 || reviewData.overallScore > 100) {
    errors.push(
      translateText
        ? translateText('analytics.performance.validation.overallScoreRange')
        : 'Overall score must be between 0 and 100'
    );
  }

  return errors;
};
